import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Pagination, Response } from '../../../../_shared/models';
import { WorkOrderPaginationRequest } from '../models/work-order.models';
import { ApproveWorkOrdersRequest, UpdateWorkOrderRequestStatusRequest } from '../models/work-order-requests-overview.models';
import { WorkOrderFaultImage, WorkOrderItem } from '../models/work-orders-overview.models';
import { WorkOrderSourceLabels, WorkOrderPriorityLabels } from '../enums/work-order.enum';
import { WORK_ORDER_REQUESTS_MOCK } from '../mockdata/work-order-mock-data';
import { delay } from '../../../../_shared/helpers/delay.helper';
import { environment } from '../../../../../environments/environment';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class WorkOrderRequestsOverviewService {
  private http: HttpClient = inject(HttpClient);
  private transloco = inject(TranslocoService);

  async getWorkOrderRequestsAsync(request: WorkOrderPaginationRequest, cancellation: Subject<void>): Promise<Response<Pagination<WorkOrderItem[]>>> {
    return firstValueFrom(
      this.http.post<Response<Pagination<WorkOrderItem[]>>>(`${environment.apiEndpoint}/workorder/paginated-work-orders`, request).pipe(takeUntil(cancellation))
    );
  }

  async updateWorkOrderRequestStatusAsync(request: UpdateWorkOrderRequestStatusRequest, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.post<Response<boolean>>(`${environment.apiEndpoint}/WorkOrder/update-work-order-request-status`, request).pipe(takeUntil(cancellation))
    );
  }

  async approveWorkOrderRequestsAsync(request: ApproveWorkOrdersRequest, cancellation: Subject<void>): Promise<Response<boolean>> {
    return firstValueFrom(
      this.http.post<Response<boolean>>(`${environment.apiEndpoint}/WorkOrder/approve-work-orders`, request).pipe(takeUntil(cancellation))
    );
  }

  async getWorkOrderFaultImagesAsync(workOrderNumber: string, cancellation: Subject<void>): Promise<Response<WorkOrderFaultImage[]>> {
    return firstValueFrom(
      this.http.get<Response<WorkOrderFaultImage[]>>(`${environment.apiEndpoint}/WorkOrder/fault-images/${encodeURIComponent(workOrderNumber)}`).pipe(takeUntil(cancellation))
    );
  }

  async getWorkOrderFaultImageDownloadLinkAsync(workOrderNumber: string, reference: string, cancellation: Subject<void>): Promise<Response<string>> {
    return firstValueFrom(
      this.http.get<Response<string>>(`${environment.apiEndpoint}/WorkOrder/fault-images/${encodeURIComponent(workOrderNumber)}/download/${encodeURIComponent(reference)}`).pipe(takeUntil(cancellation))
    );
  }

  /**
   * Export work order requests to CSV/Excel
   * TODO: To be replaced with actual HTTP call to backend API
   */
  async exportWorkOrderRequestsAsync(request: WorkOrderPaginationRequest, cancellation: Subject<void>): Promise<Blob> {
    await delay(1000);

    const headers = 'Work Order ID,Asset,Date Created,Source,Work Type,Workshop,Estimated Cost,Estimated Time,Priority\n';
    const rows = WORK_ORDER_REQUESTS_MOCK.map(req => {
      const dateCreated = req.dateCreated ? new Date(req.dateCreated).toLocaleDateString() : '';
      return `${req.workOrderId},${req.assetName},${dateCreated},${this.transloco.translate(WorkOrderSourceLabels[req.source])},${req.workType},${req.workshopName},${req.estimatedCost},${req.estimatedTime},${this.transloco.translate(WorkOrderPriorityLabels[req.priority])}`;
    }).join('\n');

    const csvContent = headers + rows;
    return new Blob([csvContent], { type: 'text/csv' });
    // return firstValueFrom(this.http.post(`${environment.apiEndpoint}/work-orders/requests/export`, request, { responseType: 'blob' }).pipe(takeUntil(cancellation)));
  }
}